import React, { useState } from 'react';
import { Badge } from '../../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import ViewAllResponsesModal from './ViewAllResponsesModal';
import { getResponseOptions } from './responseStatistics';

interface LongAnswerResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const LongAnswerResponse: React.FC<LongAnswerResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, type, answered_count } = question;
    const options = getResponseOptions(question);
    const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Sort options by count (descending) for better visual hierarchy
    const sortedOptions = [...options].sort((a, b) => b.count - a.count);
    
    // Limit displayed responses to 5 as shown in the image
    const displayedOptions = sortedOptions.slice(0, 5);
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.0993 2.51672H5.59928C5.15726 2.51672 4.73333 2.69232 4.42077 3.00488C4.10821 3.31744 3.93262 3.74136 3.93262 4.18339V17.5167C3.93262 17.9588 4.10821 18.3827 4.42077 18.6952C4.73333 19.0078 5.15726 19.1834 5.59928 19.1834H15.5993C16.0413 19.1834 16.4652 19.0078 16.7778 18.6952C17.0904 18.3827 17.266 17.9588 17.266 17.5167V6.68339L13.0993 2.51672Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.2666 2.51672V5.85006C12.2666 6.29208 12.4422 6.71601 12.7548 7.02857C13.0673 7.34113 13.4912 7.51672 13.9333 7.51672H17.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.93327 8.3501H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9333 11.6835H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9333 15.0167H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1 min-w-0">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-white"
                        >
                            {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Response List */}
            <div className="max-h-60 space-y-3 overflow-y-auto pr-1">
                {displayedOptions.map((option, index) => (
                    <div 
                        key={option.option_id} 
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                        <p className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                            {option.label}
                        </p>
                    </div>
                ))}
            </div>
            
            {/* Pagination Info */}
            {options.length > 0 && (
                <div className="text-center mt-4 text-xs text-[#717182] dark:text-gray-400">
                    Showing {Math.min(5, options.length)} of {options.length} responses - 
                    <button 
                        className="underline hover:no-underline cursor-pointer ml-1"
                        onClick={() => setIsViewAllModalOpen(true)}
                    >
                        View All
                    </button>
                </div>
            )}
            
            {/* No responses message */}
            {options.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No responses available for this question
                </div>
            )}
            
            {/* View All Modal */}
            <ViewAllResponsesModal
                isOpen={isViewAllModalOpen}
                onClose={() => setIsViewAllModalOpen(false)}
                question={question}
                totalResponses={totalResponses}
            />
        </div>
    );
};

export default LongAnswerResponse;
